import type { CandleSource } from '../stores/priceDataStore';
import { getAccessToken } from '../stores/authStore';
import type { SymbolEligibilityProjection } from './types';

const BASE = '/api';

type CandleResponse = {
  symbol: string;
  interval: number;
  count: number;
  candles: Array<{
    startTs: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
    source?: CandleSource;
  }>;
};

type IndicatorResponse = {
  symbol: string;
  ema: Record<number, Array<{ startTs: number; value: number }>>;
  rsi: Array<{ startTs: number; value: number }>;
  sma?: Record<number, Array<{ startTs: number; value: number }>>;
  bbands?: Array<{ startTs: number; upper: number; middle: number; lower: number; bandwidth: number; percentB: number }>;
  macd?: Array<{ startTs: number; macd: number; signal: number; histogram: number }>;
  vwap?: Array<{ startTs: number; value: number; upperBand1: number; lowerBand1: number; upperBand2: number; lowerBand2: number }>;
  adx?: Array<{ startTs: number; adx: number; plusDi: number; minusDi: number }>;
  atr?: Array<{ startTs: number; value: number; trueRange: number }>;
  cmf?: Array<{ startTs: number; value: number }>;
  ichimoku?: Array<{ startTs: number; tenkan: number; kijun: number; senkouA: number; senkouB: number; chikou: number }>;
  obv?: Array<{ startTs: number; value: number }>;
  psar?: Array<{ startTs: number; value: number; trend: 'up' | 'down' }>;
  pivot?: Array<{ startTs: number; pp: number; r1: number; r2: number; r3: number; s1: number; s2: number; s3: number }>;
  stoch?: Array<{ startTs: number; k: number; d: number }>;
  williamsr?: Array<{ startTs: number; value: number }>;
  fvg?: Array<{ startTs: number; direction: 'bullish' | 'bearish'; gapHigh: number; gapLow: number; gapSize: number; status: 'open' | 'filled' | 'partial'; fillPct: number }>;
  bos?: Array<{ startTs: number; direction: 'bullish' | 'bearish'; level: number; breakCandle: number }>;
  choch?: Array<{ startTs: number; direction: 'bullish' | 'bearish'; level: number; breakCandle: number }>;
  fib?: Array<{ startTs: number; direction: 'up' | 'down'; swingHigh: number; swingLow: number; levels: string }>;
  svp?: Array<{ startTs: number; poc: number; valueAreaHigh: number; valueAreaLow: number; totalVolume: number }>;
};

export async function fetchCandles(
  symbol: string,
  interval: number,
  limit = 500,
  from?: number,
  to?: number,
): Promise<CandleResponse> {
  const params = new URLSearchParams({
    symbol,
    interval: String(interval),
    limit: String(limit),
  });
  if (from != null) params.set('from', String(from));
  if (to != null) params.set('to', String(to));
  const res = await fetch(`${BASE}/candles?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch candles: ${res.status}`);
  return res.json();
}

export type { CandleResponse, IndicatorResponse };

// ── Recent market trades ─────────────────────────────────────

export type TradeHistoryEntry = {
  ts: number;
  price: string;
  qty: string;
  side: 'buy' | 'sell';
  tradeId?: string;
};

export type TradesResponse = {
  symbol: string;
  count: number;
  trades: TradeHistoryEntry[];
};

/**
 * Fetch the most-recent N market trades for a symbol from TimescaleDB.
 * Used by the chart's market-trades panel so the user sees history on
 * load instead of an empty "Waiting for trades..." placeholder. Distinct
 * from `portfoliosApi.fetchPortfolioTrades` which returns the *user's own*
 * strategy trade history scoped to a paper portfolio.
 */
export async function fetchRecentTrades(
  symbol: string,
  limit = 100,
): Promise<TradesResponse> {
  const params = new URLSearchParams({ symbol, limit: String(limit) });
  const res = await fetch(`${BASE}/trades?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch trades: ${res.status}`);
  return res.json();
}

type PreviewCandleResponse = CandleResponse & { source: 'preview' };

type PreviewIndicatorResponse = {
  symbol: string;
  ema: Record<number, Array<{ startTs: number; value: number }>>;
  rsi: Array<{ startTs: number; value: number }>;
  sma: Record<number, Array<{ startTs: number; value: number }>>;
  macd: Array<{ startTs: number; macd: number; signal: number; histogram: number }>;
  source: 'preview';
};

export type { PreviewCandleResponse, PreviewIndicatorResponse };

export async function fetchPreviewCandles(
  symbol: string,
  interval: number,
): Promise<PreviewCandleResponse> {
  const params = new URLSearchParams({
    symbol,
    interval: String(interval),
  });
  const res = await fetch(`${BASE}/preview/candles?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch preview candles: ${res.status}`);
  return res.json();
}

export async function fetchPreviewIndicators(
  symbol: string,
  interval: number,
): Promise<PreviewIndicatorResponse> {
  const params = new URLSearchParams({
    symbol,
    interval: String(interval),
  });
  const res = await fetch(`${BASE}/preview/indicators?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch preview indicators: ${res.status}`);
  return res.json();
}

export async function fetchIndicators(
  symbol: string,
  limit = 1000,
  from?: number,
  to?: number,
  interval = 1,
): Promise<IndicatorResponse> {
  const params = new URLSearchParams({
    symbol,
    interval: String(interval),
    limit: String(limit),
  });
  if (from != null) params.set('from', String(from));
  if (to != null) params.set('to', String(to));
  const res = await fetch(`${BASE}/indicators?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch indicators: ${res.status}`);
  return res.json();
}

// Trade-flow features (sourced from the trade_flow_* continuous aggregates).
export type TradeFlowResponse = {
  symbol: string;
  count: number;
  tradeFlow: Array<{
    startTs: number;
    signedVolume: number | null;
    buyVolume: number | null;
    sellVolume: number | null;
    tradeImbalance: number | null;
    largeTradeCount: number | null;
    tradeCount: number | null;
  }>;
};

export async function fetchTradeFlow(
  symbol: string,
  interval = 1,
  limit = 1000,
  from?: number,
  to?: number,
): Promise<TradeFlowResponse> {
  const params = new URLSearchParams({
    symbol,
    interval: String(interval),
    limit: String(limit),
  });
  if (from != null) params.set('from', String(from));
  if (to != null) params.set('to', String(to));
  const res = await fetch(`${BASE}/trade-flow?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch trade-flow: ${res.status}`);
  return res.json();
}

// ── Book seed (current order book snapshot via Kraken REST) ─

export type BookSeedLevel = { price: number; qty: number };

export type BookSeedResponse = {
  symbol: string;
  bids: BookSeedLevel[];
  asks: BookSeedLevel[];
  source?: string;
};

/**
 * Fetch the current Kraken public order book for a symbol.
 * Used to seed the depth chart / order book panel immediately on chart load
 * before the live WS book-snapshot (fired by api-gateway on subscribe) arrives.
 * Data is never persisted — display-only proxy.
 */
export async function fetchRecentBook(
  symbol: string,
  depth = 100,
): Promise<BookSeedResponse> {
  const params = new URLSearchParams({ symbol, depth: String(depth) });
  const res = await fetch(`${BASE}/book?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch book: ${res.status}`);
  return res.json();
}

// ── Book history ────────────────────────────────────────────

export type BookHistoryBucket = { price: number; qty: number };

export type BookHistorySnapshot = {
  ts: number;
  midPrice: number;
  bucketSize: number;
  bidBuckets: BookHistoryBucket[];
  askBuckets: BookHistoryBucket[];
};

export type BookHistoryResponse = {
  symbol: string;
  snapshots: BookHistorySnapshot[];
};

export async function fetchBookHistory(
  symbol: string,
  from?: number,
  to?: number,
): Promise<BookHistoryResponse> {
  const params = new URLSearchParams({ symbol });
  if (from != null) params.set('from', String(from));
  if (to != null) params.set('to', String(to));
  const res = await fetch(`${BASE}/book-history?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch book history: ${res.status}`);
  return res.json();
}

// ── Integrity ────────────────────────────────────────────────

export type IntegrityGraphPoint = {
  runId: string;
  scanType: string;
  startedAt: number;
  completedAt: number;
  durationMs: number;
  symbolCount: number;
  avgOhlcPct: number;
  avgRealOhlcPct: number;
  avgIndicatorPct: number;
  totalChunks: number;
  totalConfirmed: number;
  errors: string[];
};

export async function fetchIntegrityGraph(
  fromMs?: number,
  toMs?: number,
  scanType?: string,
): Promise<IntegrityGraphPoint[]> {
  const params = new URLSearchParams();
  if (fromMs != null) params.set('from', String(fromMs));
  if (toMs != null) params.set('to', String(toMs));
  if (scanType) params.set('scanType', scanType);
  const url = `${BASE}/integrity/runs/graph?${params}`;

  // The graph proxies through api-gateway → data-integrity. While
  // data-integrity is restarting or its connection pool is briefly
  // saturated by an audit sweep, the gateway returns a transient 502
  // ("data-integrity unreachable"). The underlying query is fast
  // (~0.7s) and steady — a single short-backoff retry clears the blip
  // without the user ever seeing a raw status code. Only 5xx is
  // retried; 4xx is a real client error and fails fast.
  const maxAttempts = 2;
  let lastStatus = 0;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const res = await fetch(url);
    if (res.ok) return res.json();
    lastStatus = res.status;
    const isTransient = res.status >= 500;
    if (!isTransient || attempt === maxAttempts) break;
    await new Promise((r) => setTimeout(r, 1_000));
  }
  // Friendly message — a 5xx here means the integrity service is
  // momentarily busy/restarting, not that the data is wrong. The graph
  // auto-refreshes every 60s and recovers on its own.
  if (lastStatus >= 500) {
    throw new Error('Graph temporarily unavailable — integrity service busy (auto-retries)');
  }
  throw new Error(`Failed to fetch integrity graph: ${lastStatus}`);
}

export type FetchEligibilityArgs = {
  symbol: string;
  market?: string;
  fromTs?: number;
  toTs?: number;
  nowMs?: number;
};

export async function fetchEligibility({
  symbol,
  market,
  fromTs,
  toTs,
  nowMs,
}: FetchEligibilityArgs): Promise<SymbolEligibilityProjection> {
  const params = new URLSearchParams({ symbol });
  if (market) params.set('market', market);
  if (fromTs != null) params.set('fromTs', String(fromTs));
  if (toTs != null) params.set('toTs', String(toTs));
  if (nowMs != null) params.set('nowMs', String(nowMs));

  const res = await fetch(`${BASE}/integrity/eligibility?${params}`);
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json() as { error?: string };
      if (body.error) {
        detail = ` ${body.error}`;
      }
    } catch {
      // Keep the HTTP status as the fallback error detail.
    }
    throw new Error(`Failed to fetch eligibility: ${res.status}${detail}`);
  }
  return res.json();
}

// ── Sentiment ────────────────────────────────────────────────

export async function fetchSentimentHistory(
  limit = 200,
  sourceType?: string,
  symbol?: string,
): Promise<import('./types').SentimentEventData[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (sourceType) params.set('sourceType', sourceType);
  if (symbol) params.set('symbol', symbol);
  const res = await fetch(`${BASE}/sentiment/history?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch sentiment history: ${res.status}`);
  return res.json();
}

export type SentimentFactorHistoryPoint = {
  ts: number;
  symbol: string | null;
  regimeScore: number;
  sentimentScore: number;
  attentionScore: number;
  confidenceScore: number;
  freshnessScore: number;
  evidenceCount: number;
};

export type SentimentTimeframe = '1h' | '1d' | '7d' | '1m' | '3m' | '1y';

/**
 * Time-bucketed sentiment factor history. Omit `symbol` for the market-wide
 * composite (confidence-weighted average across all symbols).
 */
export async function fetchSentimentFactorHistory(
  tf: SentimentTimeframe,
  symbol?: string,
): Promise<SentimentFactorHistoryPoint[]> {
  const params = new URLSearchParams({ tf });
  if (symbol) params.set('symbol', symbol);
  const res = await fetch(`${BASE}/sentiment/factors/history?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch factor history: ${res.status}`);
  return res.json();
}

export type SentimentSourceCountPoint = {
  ts: number;
  news: number;
  social: number;
  macro: number;
};

/** Per-source-type event counts over the selected timeframe, for one symbol. */
export async function fetchSentimentSourceCounts(
  symbol: string,
  tf: SentimentTimeframe,
): Promise<SentimentSourceCountPoint[]> {
  const params = new URLSearchParams({ tf, symbol });
  const res = await fetch(`${BASE}/sentiment/source-counts?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch source counts: ${res.status}`);
  return res.json();
}

// ── Backtest ────────────────────────────────────────────────────

export type BacktestRunRequest = {
  symbol: string;
  from: string; // ISO date
  to: string;   // ISO date
  strategies: string[];
  startingBalance: number;
  riskPerTradePct?: number | undefined;
  maxPositionsPerSymbol?: number | undefined;
  maxTotalPositions?: number | undefined;
  slAtrMultiplier?: number | undefined;
  tpAtrMultiplier?: number | undefined;
  buyThreshold?: number | undefined;
  sellThreshold?: number | undefined;
  trailingActivationAtr?: number | undefined;
  trailingStepAtr?: number | undefined;
  maxDrawdownPct?: number | undefined;
  cooldownCandles?: number | undefined;
  /** Chart/replay interval in minutes. Must be one of the approved buckets
   *  (1/5/15/60/240/1440). Omit to use the strategy's primary timeframe. The
   *  engine coarsens this for very large windows (replica-load guard). */
  replayInterval?: number | undefined;
};

/** The only intervals the backtest chart will render at — matches the engine's
 *  REPLAY_LADDER. Selecting outside this set is not allowed. */
export const REPLAY_INTERVALS: ReadonlyArray<{ label: string; minutes: number }> = [
  { label: '1m', minutes: 1 },
  { label: '5m', minutes: 5 },
  { label: '15m', minutes: 15 },
  { label: '1h', minutes: 60 },
  { label: '4h', minutes: 240 },
  { label: '1d', minutes: 1440 },
];

export type BacktestMetrics = {
  totalReturn: number;
  totalReturnPct: number;
  totalTrades: number;
  winCount: number;
  lossCount: number;
  winRate: number;
  averageWin: number;
  averageLoss: number;
  profitFactor: number;
  maxDrawdown: number;
  maxDrawdownPct: number;
  sharpeRatio: number;
  sortinoRatio: number;
  calmarRatio: number;
  avgHoldingMinutes: number;
  buyAndHoldReturnPct?: number | undefined;
  alpha?: number | undefined;
};

export type EquityPoint = {
  time: number;
  equity: number;
  cash: number;
  unrealized: number;
  drawdownPct: number;
};

/**
 * The trade shape the strategy-engine actually emits (ClosedTrade). The
 * api-gateway proxies it verbatim. Field names differ from the web-ui's
 * `BacktestTrade` convention (openedAt/closedAt/stopLossPrice/takeProfitPrice),
 * so we normalize at the REST boundary — see `normalizeBacktestTrade`.
 */
export type RawBacktestTrade = {
  tradeId: string;
  symbol: string;
  strategyId: string;
  side: string;
  entryPrice: number;
  exitPrice: number;
  openedAt: number;
  closedAt: number;
  stopLossPrice?: number;
  takeProfitPrice?: number;
  positionSize: number;
  positionQty: number;
  realizedPnl: number;
  realizedPnlPct: number;
  exitReason: string;
};

export type BacktestTrade = {
  tradeId: string;
  symbol: string;
  strategyId: string;
  side: string;
  entryPrice: number;
  exitPrice: number;
  entryTime: number;
  exitTime: number;
  /** Strategy-sourced exit levels at entry. Undefined for legacy runs that
   *  predate SL/TP passthrough. Used to draw SL/TP lines during replay. */
  stopLoss?: number;
  takeProfit?: number;
  positionSize: number;
  positionQty: number;
  realizedPnl: number;
  realizedPnlPct: number;
  exitReason: string;
  holdingMinutes: number;
  /** Reward-to-risk realized vs. the entry SL distance: (exit-entry)/(entry-SL)
   *  for longs (sign-flipped for shorts). Undefined when SL is absent. */
  rMultiple?: number;
};

/**
 * Map the engine's raw backtest trade into the web-ui `BacktestTrade`
 * convention. Renames openedAt/closedAt → entryTime/exitTime, surfaces
 * stopLoss/takeProfit, derives holdingMinutes and rMultiple. Tolerant of
 * older saved runs that already carry the web-ui field names (they pass
 * through unchanged) and of runs missing SL/TP.
 */
export function normalizeBacktestTrade(
  raw: RawBacktestTrade & Partial<BacktestTrade>,
): BacktestTrade {
  const entryTime = raw.entryTime ?? raw.openedAt;
  const exitTime = raw.exitTime ?? raw.closedAt;
  const stopLoss = raw.stopLoss ?? raw.stopLossPrice;
  const takeProfit = raw.takeProfit ?? raw.takeProfitPrice;
  const holdingMinutes =
    raw.holdingMinutes ??
    (entryTime != null && exitTime != null
      ? Math.max(0, Math.round((exitTime - entryTime) / 60_000))
      : 0);

  let rMultiple = raw.rMultiple;
  if (rMultiple == null && stopLoss != null && raw.entryPrice != null) {
    const risk = raw.entryPrice - stopLoss; // long: >0 when SL below entry
    if (Math.abs(risk) > 1e-12) {
      const reward = raw.exitPrice - raw.entryPrice;
      // For shorts, profit is inverted; risk is also inverted (SL above entry),
      // so reward/risk already carries the correct sign.
      rMultiple = reward / risk;
    }
  }

  return {
    tradeId: raw.tradeId,
    symbol: raw.symbol,
    strategyId: raw.strategyId,
    side: raw.side,
    entryPrice: raw.entryPrice,
    exitPrice: raw.exitPrice,
    entryTime,
    exitTime,
    ...(stopLoss != null ? { stopLoss } : {}),
    ...(takeProfit != null ? { takeProfit } : {}),
    positionSize: raw.positionSize,
    positionQty: raw.positionQty,
    realizedPnl: raw.realizedPnl,
    realizedPnlPct: raw.realizedPnlPct,
    exitReason: raw.exitReason,
    holdingMinutes,
    ...(rMultiple != null ? { rMultiple } : {}),
  };
}

export type MonthlyReturn = {
  year: number;
  month: number;
  returnPct: number;
  trades: number;
};

/**
 * The window the engine ACTUALLY replayed (echoed back by BacktestRunner).
 * `resolvedFromMax` is true when from/to were resolved from the symbol's full
 * available history; `note` carries any guard caveat (cadence coarsened /
 * window clamped for replica safety). Surfacing this is what tells the user how
 * far back a strategy was really tested.
 */
export type BacktestWindowResult = {
  from: string;
  to: string;
  replayInterval: number;
  resolvedFromMax: boolean;
  note?: string;
};

export type BacktestResult = {
  config: BacktestRunRequest;
  metrics: BacktestMetrics;
  trades: BacktestTrade[];
  equityCurve: EquityPoint[];
  monthlyReturns: MonthlyReturn[];
  candleCount: number;
  durationMs: number;
  /** What the engine really tested. Emitted by the backend; optional for older saved runs. */
  window?: BacktestWindowResult;
};

export type BacktestStrategiesResponse = {
  available: string[];
  enabled: string[];
};

/**
 * Start a streaming backtest. Reads NDJSON line by line and calls onEvent for each.
 * Returns the backtestId for cancellation.
 */
export async function startBacktestStream(
  config: BacktestRunRequest,
  onEvent: (event: unknown) => void,
  signal?: AbortSignal,
): Promise<string> {
  const init: RequestInit = {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  };
  if (signal) init.signal = signal;

  const res = await fetch(`${BASE}/backtest/stream`, init);

  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Backtest stream failed: ${res.status}`);
  }

  const backtestId = res.headers.get('X-Backtest-Id') ?? 'unknown';
  const reader = res.body?.getReader();
  if (!reader) throw new Error('No response body');

  const decoder = new TextDecoder();
  let buffer = '';

  const read = async () => {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        if (line.trim()) {
          try { onEvent(JSON.parse(line)); } catch { /* skip malformed */ }
        }
      }
    }
    // Flush remaining
    if (buffer.trim()) {
      try { onEvent(JSON.parse(buffer)); } catch { /* skip */ }
    }
  };

  void read();
  return backtestId;
}

export async function cancelBacktest(backtestId: string): Promise<void> {
  await fetch(`${BASE}/backtest/cancel/${backtestId}`, { method: 'POST' });
}

export async function runBacktest(config: BacktestRunRequest): Promise<BacktestResult> {
  const res = await fetch(`${BASE}/backtest/run`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Backtest failed: ${res.status}`);
  }
  const result = (await res.json()) as Omit<BacktestResult, 'trades'> & {
    trades: Array<RawBacktestTrade & Partial<BacktestTrade>>;
  };
  return { ...result, trades: result.trades.map(normalizeBacktestTrade) };
}

// ── Persisted backtest runs (S5-05 endpoints) ──────────────────

export type SavedBacktestSummary = {
  _id: string;
  userId: string;
  strategyId: string;
  symbol: string;
  from: string;
  to: string;
  metrics: BacktestMetrics;
  monthlyReturns: MonthlyReturn[];
  candleCount: number;
  durationMs: number;
  createdAt: string;
};

export type SavedBacktestFull = SavedBacktestSummary & {
  config: BacktestRunRequest;
  trades: BacktestTrade[];
  equityCurve: EquityPoint[];
};

function authHeaders(): HeadersInit {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function listBacktestRuns(limit = 50): Promise<{
  count: number;
  runs: SavedBacktestSummary[];
}> {
  const res = await fetch(`${BASE}/backtest/runs?limit=${limit}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to list runs: ${res.status}`);
  return res.json();
}

export async function fetchBacktestRun(id: string): Promise<SavedBacktestFull> {
  const res = await fetch(`${BASE}/backtest/runs/${encodeURIComponent(id)}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Failed to fetch run: ${res.status}`);
  }
  const run = (await res.json()) as Omit<SavedBacktestFull, 'trades'> & {
    trades: Array<RawBacktestTrade & Partial<BacktestTrade>>;
  };
  return { ...run, trades: run.trades.map(normalizeBacktestTrade) };
}

export async function deleteBacktestRun(id: string): Promise<void> {
  const res = await fetch(`${BASE}/backtest/runs/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to delete run: ${res.status}`);
}

export async function fetchBacktestStrategies(): Promise<BacktestStrategiesResponse> {
  const res = await fetch(`${BASE}/backtest/strategies`);
  if (!res.ok) throw new Error(`Failed to fetch strategies: ${res.status}`);
  return res.json();
}

// ── Research registry (read-only) ────────────────────────────────────────────
// Backs the Research browser + minimal replay viewer. All endpoints are
// read-only and path-safe on the server; the web-ui never touches the
// filesystem or runs anything.

export type ResearchIteration = {
  iteration_id: string;
  harness: string;
  strategy_family: string | null;
  status: 'complete' | 'partial' | 'corrupt' | 'summarized';
  verdict: string | null;
  verdict_summary: string | null;
  created_at: string;
  imported_at: string;
  git_sha: string | null;
  rel_dir: string;
  source_format: string | null;
  schema_version: number;
  params: Record<string, unknown>;
  cost_scenarios: Array<number | string>;
  symbols: string[];
  windows: string[];
  headline_metrics: Record<string, unknown>;
  artifact_count?: number;
  /** Owner (null = legacy/global). */
  user_id?: string | null;
  /** Soft-hidden from the default Browse view. */
  archived?: boolean;
  /** Model backs an ACTIVE strategy → purge is blocked (archive still allowed). */
  in_use?: boolean;
};

/** The Browse iterations list response (owner-scoped + quota metadata). */
export type ResearchIterationsResponse = {
  iterations: ResearchIteration[];
  count: number;
  archivedCount: number;
  quota: number;
  activeCount: number;
};

/** Per-user research eviction preferences. */
export type ResearchEvictionPrefs = {
  eviction_mode: 'oldest' | 'no_edge' | 'manual';
  evict_action: 'archive' | 'purge';
  quota: number;
};

export type ResearchArtifact = {
  id: string;
  iteration_id: string;
  kind: string;
  rel_path: string;
  bytes: string;
  mime: string | null;
  label: string | null;
  meta: Record<string, unknown>;
};

export type ResearchReplayVariant = {
  id: string;
  label: string;
  strategyId: string | null;
  tag: string | null;
  netMean: number | null;
  n: number | null;
  file: string | null;
};

export type ResearchReplayIndex = {
  iterationId: string;
  indexPath: string | null;
  strategyId?: string | null;
  source?: string | null;
  replays: ResearchReplayVariant[];
};

export type ResearchReplayMarker = {
  time: string;
  symbol?: string;
  entry?: number;
  tp?: number;
  sl?: number;
  outcome?: string;
  gross?: number;
  net?: number;
  regime?: string;
  window?: string;
  chop?: boolean;
  [k: string]: unknown;
};

export type ResearchEquity = {
  iterationId: string;
  file: string;
  strategyId: string | null;
  cost: number | null;
  n: number;
  netMean: number | null;
  metrics: Record<string, unknown> | null;
  equityCurve: number[];
  netSeries: number[];
  drawdownCurve: number[];
  markers: ResearchReplayMarker[];
};

export async function fetchResearchIterations(filters?: {
  harness?: string;
  family?: string;
  status?: string;
  archived?: boolean;
}): Promise<ResearchIterationsResponse> {
  const params = new URLSearchParams();
  if (filters?.harness) params.set('harness', filters.harness);
  if (filters?.family) params.set('family', filters.family);
  if (filters?.status) params.set('status', filters.status);
  if (filters?.archived) params.set('archived', 'true');
  const qs = params.toString();
  // Auth'd: the list is owner-scoped server-side.
  const res = await fetch(`${BASE}/research/iterations${qs ? `?${qs}` : ''}`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch research iterations: ${res.status}`);
  return (await res.json()) as ResearchIterationsResponse;
}

/** Archive (soft-hide) an iteration. Reversible; keeps artifacts on disk. */
export async function archiveResearchIteration(id: string): Promise<void> {
  const res = await fetch(`${BASE}/research/iterations/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Archive failed (${res.status})`);
}

export async function unarchiveResearchIteration(id: string): Promise<void> {
  const res = await fetch(`${BASE}/research/iterations/${encodeURIComponent(id)}/unarchive`, {
    method: 'POST',
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Unarchive failed (${res.status})`);
}

/** Purge (hard-delete: disk + DB row). Throws with the server message on 422
 *  (in use by an active strategy) so the UI can surface it. */
export async function purgeResearchIteration(id: string): Promise<void> {
  const res = await fetch(`${BASE}/research/iterations/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    let msg = `Purge failed (${res.status})`;
    try {
      const j = (await res.json()) as { error?: string; message?: string };
      msg = j.message ?? j.error ?? msg;
    } catch {
      /* non-JSON */
    }
    throw new Error(msg);
  }
}

/** Result of a bulk delete. */
export type BulkDeleteResult = {
  deleted: number;
  skippedInUse: number;
  skippedOther: number;
  failed: number;
  deletedIds: string[];
  skippedInUseIds: string[];
};

/** Bulk archive/unarchive many iterations in one request. archived=false = unarchive. */
export async function bulkArchiveResearchIterations(
  ids: string[],
  archived: boolean,
): Promise<{ archived: boolean; changed: number; skipped: number }> {
  const res = await fetch(`${BASE}/research/iterations/bulk-archive`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, archived }),
  });
  if (!res.ok) throw new Error(`Bulk archive failed (${res.status})`);
  return (await res.json()) as { archived: boolean; changed: number; skipped: number };
}

/** Bulk delete many iterations in one request. In-use models are skipped + reported. */
export async function bulkDeleteResearchIterations(ids: string[]): Promise<BulkDeleteResult> {
  const res = await fetch(`${BASE}/research/iterations/bulk-delete`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new Error(`Bulk delete failed (${res.status})`);
  return (await res.json()) as BulkDeleteResult;
}

export async function getResearchEvictionPrefs(): Promise<ResearchEvictionPrefs> {
  const res = await fetch(`${BASE}/research/iterations-prefs`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Failed to load prefs (${res.status})`);
  return (await res.json()) as ResearchEvictionPrefs;
}

export async function setResearchEvictionPrefs(prefs: {
  eviction_mode: string;
  evict_action: string;
}): Promise<ResearchEvictionPrefs> {
  const res = await fetch(`${BASE}/research/iterations-prefs`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(prefs),
  });
  if (!res.ok) throw new Error(`Failed to save prefs (${res.status})`);
  return (await res.json()) as ResearchEvictionPrefs;
}

export async function fetchResearchIteration(
  id: string,
): Promise<{ iteration: ResearchIteration; artifacts: ResearchArtifact[] }> {
  const res = await fetch(`${BASE}/research/iterations/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`Failed to fetch iteration ${id}: ${res.status}`);
  return res.json();
}

export async function fetchResearchReplayIndex(id: string): Promise<ResearchReplayIndex> {
  const res = await fetch(
    `${BASE}/research/iterations/${encodeURIComponent(id)}/replay-index`,
  );
  if (!res.ok) throw new Error(`Failed to fetch replay index for ${id}: ${res.status}`);
  return res.json();
}

export async function fetchResearchEquity(
  id: string,
  file: string,
): Promise<ResearchEquity> {
  const res = await fetch(
    `${BASE}/research/iterations/${encodeURIComponent(id)}/equity?file=${encodeURIComponent(file)}`,
  );
  if (!res.ok) throw new Error(`Failed to fetch replay data: ${res.status}`);
  return res.json();
}

/** URL for fetching a raw registered artifact (markdown report, json, …). */
export function researchArtifactUrl(id: string, relPath: string): string {
  return `${BASE}/research/artifact?id=${encodeURIComponent(id)}&path=${encodeURIComponent(relPath)}`;
}

// ── Data Readiness Gate (S16 surfacing) ───────────────────────────────────────
// Read-only mirror of tools/readiness-gate/evaluate_gate.py's JSON output. See
// docs/research/DATA-READINESS-GATE.md and docs/research/README-gate-surfacing-plan.md.

export type GateVerdict = 'GO' | 'WAIT' | 'BLOCK' | 'UNKNOWN';
export type GateProfile = 'SWING' | 'INTRADAY';
export type GateProfileStatus = 'READY' | 'GO-PENDING-ACCRUAL' | 'NOT-READY';

export type GateFinding = {
  finding_id: string;
  dimension: string | null;
  profiles: GateProfile[] | 'ALL' | null;
  verdict: GateVerdict | null;
  scope: Record<string, unknown> | null;
  measurement: Record<string, unknown> | null;
  measured: Record<string, unknown> | null;
  threshold: Record<string, unknown> | null;
  confidence: { level?: string; caveats?: string[] } | null;
  reported_by: string | null;
  reported_at: string | null;
  projected_ready_date: {
    date?: string;
    observed_rate?: string;
    arithmetic?: string;
    assumes_no_backfill_route?: boolean;
  } | null;
  remediation: { summary?: string; owner?: string } | null;
  unknown_reason: string | null;
  supersedes: string | null;
  source_file: string | null;
};

export type GateDimensionReport = {
  kind: 'primary' | 'derived';
  derives_from?: string[];
  per_profile: Record<GateProfile, GateVerdict>;
  finding_ids: Record<GateProfile, string[]>;
  notes?: Record<GateProfile, string>;
  unknown_reason: string | null;
  live_escalation?: { verdict: GateVerdict; reason?: string } | null;
  /** Carry-forward guard / accrual clock reason, per profile (readiness_questions.py G2/G3). */
  forced_notes?: Partial<Record<GateProfile, string>>;
};

export type GateProfileReport = {
  status: GateProfileStatus;
  dimension_verdicts: Record<string, GateVerdict>;
  block: string[];
  unknown: string[];
  wait: string[];
  go: string[];
};

export type GateVerdictDoc = {
  schema_version: string;
  gate_evidence_schema_version: string;
  generated_at: string;
  git_sha: string;
  timescale_target: string;
  mongo_target: string;
  evidence_summary: {
    total_files: number;
    valid_files: number;
    finding_count: number;
    file_errors: string[];
    supersession_warnings: string[];
  };
  dimensions: Record<string, GateDimensionReport>;
  profiles: Partial<Record<GateProfile, GateProfileReport>>;
  live_checks: Record<string, { ok: boolean; skipped?: boolean; error?: string }>;
  live_escalations: Record<string, { verdict: GateVerdict; reason?: string }>;
  live_notes: Record<string, unknown>;
  findings: Record<string, GateFinding>;
  /**
   * The three-way split (chatroom 025). `profiles`/`dimensions` above stay the unrestricted
   * "any window" reading and are exactly the worst of the three; this is where the answer
   * actually lives. Optional so a page rendered against a pre-1.1.0 verdict still works.
   */
  readiness_questions?: GateReadinessQuestions;
};

export type GateReadinessQuestionId =
  | 'historical_research_readiness'
  | 'forward_research_readiness'
  | 'promotion_readiness';

export type GateAccrualProjection = {
  verdict: GateVerdict;
  projected_ready_date: string;
  projected_ready_date_policy_strict: string;
  days_remaining: number;
  days_accrued: number;
  required_days: number;
  required_days_policy_strict: number;
  days_remaining_policy_strict: number;
  binding_timeframe_policy_strict: string;
  binding_timeframe: string;
  basis: string;
};

export type GateAccrual = {
  observed: {
    start: string;
    latest: string;
    days_accrued: number;
    distinct_days_present: number;
    lossless: boolean;
    eligible_symbols: number;
    eligible_symbols_full_history: number;
    source: string;
  };
  projection: Partial<Record<GateProfile, GateAccrualProjection>>;
  assumptions: { id: string; assumption: string; basis: string; if_wrong: string }[];
};

export type GateReadinessQuestion = {
  question: string;
  admissibility: string;
  expected: string;
  finding_count: number;
  dimensions: Record<string, GateDimensionReport>;
  profiles: Partial<Record<GateProfile, GateProfileReport>>;
  /** Present on promotion_readiness only. */
  accrual?: GateAccrual;
};

export type GateReadinessQuestions = {
  cutover: { date: string; source: string; why: string };
  routing: {
    method: string;
    rule: string;
    why_not_self_declared: string;
    counts: { historical_bearing: number; forward_bearing: number; both: number; total: number };
  };
  forward_only_cost_warning: string[];
  questions: Partial<Record<GateReadinessQuestionId, GateReadinessQuestion>>;
  warnings: string[];
};

export async function fetchGateLatest(): Promise<GateVerdictDoc> {
  const res = await fetch(`${BASE}/gate/latest`);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as { error?: string });
    throw new Error(body.error ?? `Failed to fetch gate verdict: ${res.status}`);
  }
  return res.json();
}
